/**
 * planetTerrainIDB — minimal IndexedDB persistence layer for baked planet
 * terrain textures. Keyed by `${seed}:${diameter}` so different sprite sizes
 * don't collide. Values are PNG Blobs (encoded by OffscreenCanvas.convertToBlob)
 * — small on disk, decoded back via createImageBitmap which runs off-thread.
 *
 * All ops are best-effort: any IDB error (private browsing, quota, old Safari)
 * falls back to recomputing the texture. Never throws to the caller.
 */

const DB_NAME = 'astra-planets';
const DB_VERSION = 1;
const STORE = 'terrains';

interface TerrainRecord {
  blob: Blob;
  createdAt: number;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDB(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });
  return dbPromise;
}

// `hex2` invalidates pre-multi-res sprites — small/medium/large planets now
// have different cell counts even at the same diameter.
function key(seed: number, diameter: number): string {
  return `hex2:${seed}:${diameter}`;
}

/** Read a cached PNG blob for this seed+diameter. Returns null on miss/error. */
export async function readTerrainBlob(seed: number, diameter: number): Promise<Blob | null> {
  const db = await openDB();
  if (!db) return null;
  return new Promise<Blob | null>((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readonly');
      const store = tx.objectStore(STORE);
      const req = store.get(key(seed, diameter));
      req.onsuccess = () => {
        const rec = req.result as TerrainRecord | undefined;
        resolve(rec?.blob ?? null);
      };
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/** Write a PNG blob for this seed+diameter. Fire-and-forget. */
export async function writeTerrainBlob(seed: number, diameter: number, blob: Blob): Promise<void> {
  const db = await openDB();
  if (!db) return;
  return new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      const rec: TerrainRecord = { blob, createdAt: Date.now() };
      store.put(rec, key(seed, diameter));
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

/** Drop everything in the store. Useful for dev / debug. */
export async function clearTerrainCache(): Promise<void> {
  const db = await openDB();
  if (!db) return;
  return new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}
